import Cookies from 'js-cookie';
import { useWebSocket, useIntervalFn } from '@vueuse/core';

import { ElMessage, ElNotification } from 'element-plus';
import { handleMsg } from './msgHandler';
import { SocketMsgInterface } from '@/lib/typings';
import { chatStore } from '@/store/chatStore';
import { cancelLoading, checkWeakNetwork, showMsg } from '@/lib/utils';
import bus from '@/lib/bus';
import { accessSession, getSessionList } from '@/lib/helpers/chat';
import dayjs from 'dayjs';
import { reportMonitorInfo } from '../helpers/platform';
const url = import.meta.env.VITE_IM_DOMAIN;

let socket: any = null;
// 最近一次发生ping的时间
let lastPingTime = 0;
// 最近一次接收到pong的时间
// let lastPongTime = 0;
// 最近一次接到下发的时间，包含pong
let lastMsgTime = 0;
// 是否在重连
let reConnectFlag = false;
// 重连标识重置计时器
let timeout: any = null;
// 在线标识
let online = false;
let reConnectTime = 0;
// 刷新页面重连提示
let reloadPageTip = false;
let reloadNotificationIns: any = null;

ping();
export function initSocket() {
  if (socket) {
    return socket;
  }
  const token = Cookies.get('IM-Token') || '';
  const { status, data, send, open, close } = useWebSocket(`${url}`, {
    protocols: [token],
    onConnected() {
      online = true;
      const chatStoreEx = chatStore();
      chatStoreEx.setImStatus('已連接');
      reset();
      reAccess();
      if (reConnectTime > 0) {
        getSessionList('personal', 1);
        bus.emit('refreshUserCsInfoState');
        reportMonitorInfo('$ClientStatus', {
          $module: 'user_reconnection',
          $title: '客戶端用戶重連',
          type: 'success',
        });
      }
      reloadPageTip && (reloadPageTip = false);
      reloadNotificationIns?.close();
      if (reConnectTime > 4) {
        showMsg('連接成功', 'success');
      }
      reConnectTime = 0;
    },
    onMessage: (ws, event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.code === 200) {
          lastMsgTime = dayjs().unix();
          handleMsg(data);
        } else {
          handleErrorMsg(data);
          cancelLoading();
        }
      } catch (e: any) {
        ElMessage.warning(e?.msg);
        console.error(e);
      }
    },
  });
  socket = { status, data, send, open, close };
  return { status, data, send, open, close };
}

function closeSocket() {
  return new Promise<void>((resolve) => {
    socket && typeof socket.close === 'function' && socket.close();
    socket = null;
    reportMonitorInfo('$ClientStatus', {
      $module: 'user_disconnection',
      $title: '客戶端用戶断连',
    });
    resolve();
  });
}

export function sendMsg(data: SocketMsgInterface) {
  return new Promise<void>((resolve) => {
    if (online && socket && typeof socket.send === 'function') {
      if (data?.type === 'SendUserMsg') {
        const store = chatStore();
        // 记录发送消息
        store.updateMsgSendMsgFailedQueue(data.data);
      }
      socket.send(JSON.stringify(data));
      resolve();
    } else if (online === false && data.type !== 'ping') {
      showMsg('連接已斷開，請刷新', 'warning');
    }
  });
}
function reset() {
  lastMsgTime = 0;
  reConnectFlag = false;
  timeout && clearTimeout(timeout);
  timeout = null;
}

function reAccess() {
  const store = chatStore();
  const session_id = store.currentChatRoomInfo.sessionId;
  if (session_id) {
    accessSession({
      type: store.currentChatRoomInfo.roomType,
      group_id: store.currentChatRoomInfo.groupId,
      session_id,
      rl: true,
    });
  }
}
function reConnect() {
  if (reConnectTime > 50) {
    showMsg('重連失敗，請重新進入系統...', 'warning');
    showReloadTip();
    return false;
  }
  if (reConnectFlag) return false;
  reConnectFlag = true;
  reConnectTime++;
  const chatStoreEx = chatStore();
  chatStoreEx.setImStatus('連接中');
  if (reConnectTime > 1 && navigator?.onLine !== false) {
    reportMonitorInfo('$ClientStatus', {
      $module: 'user_reconnection',
      $title: '客戶端用戶重連',
      type: 'fail',
    });
  }
  closeSocket().then(() => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(
      () => {
        if (reConnectTime > 4) {
          console.log('网络异常，正在重新連接...');
          showMsg('网络波动，正在重新連接...', 'warning');
        }
        initSocket();
        reConnectFlag = false;
      },
      reConnectTime > 1 ? 2000 : 500
    );
  });
}

function handleErrorMsg(data: SocketMsgInterface) {
  ElMessage.error(data.msg);
  if (data.content_id) {
    const chatStoreEx = chatStore();
    chatStoreEx.updateSendingMsgQueue(0, data.content_id);
  }
}
export function pong() {
  lastMsgTime = dayjs().unix();
}

function ping() {
  useIntervalFn(
    () => {
      const isWeakNetWork = checkWeakNetwork();
      const lastMsgStep = isWeakNetWork ? 5 : 2;
      lastPingTime = dayjs().unix();
      if (online && (!lastMsgTime || dayjs().unix() - lastMsgTime > lastMsgStep)) {
        sendMsg({
          type: 'ping',
        });
      }
      subPingPong(isWeakNetWork);
    },
    2000,
    {
      immediate: true,
    }
  );
}

// pingpong相差20秒以上
function subPingPong(isWeakNetWork: boolean) {
  // 弱网间隔时长
  const lastMsgStep = isWeakNetWork ? 50 : 10;
  const flag = lastMsgTime !== 0 && lastPingTime !== 0 && lastPingTime - lastMsgTime > lastMsgStep;
  if (flag) {
    online = false;
    const chatStoreEx = chatStore();
    chatStoreEx.setImStatus('斷開');
    reConnect();
  }
}

// 重新 一轮重连
export function retryReconnect() {
  if (online) return;
  reConnectTime = 1;
  reConnect();
}

// 定时检查的间隔 需大于 重连机制持续时间
export function retryReconnectInterval() {
  useIntervalFn(() => {
    retryReconnect();
  }, 1000 * 60 * 8);
}

const showReloadTip = () => {
  if (!reloadPageTip) {
    reloadNotificationIns = ElNotification({
      message: '<div style="cursor:pointer;">重連失敗，点我刷新進行系統重連</div>',
      duration: 0,
      dangerouslyUseHTMLString: true,
      onClick: () => {
        window.location.reload();
      },
      type: 'warning',
    });
    reloadPageTip = true;
  }
};
